import React, { useEffect, useRef, useState } from 'react';
import { LuChevronDown, LuLogOut } from 'react-icons/lu';
import useUserStore from '../../store/userStore';
import useSchoolStore from '../../store/schoolStore';
import useGeneralStore from '../../store/generalStore';
import * as staffUtils from '../../utils/staffUtils';
import Avatar from '../common/Avatar';
import ThemeToggle from '../common/ThemeToggle';
import Logger from '../../utils/logger';

/**
 * The signed-in person at the right of the top bar. Opens a menu with their details,
 * the light / dark switch, the app version and Sign out.
 */
const AccountMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { logoutUser, userProfile, currentUser } = useUserStore();
  const { clearSchoolData } = useSchoolStore();
  const { currentAppVersion } = useGeneralStore();

  const name = userProfile?.name || 'User';
  const role = staffUtils.getStaffRole(currentUser?.type);

  // Close the menu on a click outside it or on Escape
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // AuthGuard in App.js sends the user to the login page once the user store is cleared
  const handleLogout = async () => {
    setIsOpen(false);
    try {
      const result = await logoutUser();
      clearSchoolData(true);

      if (result.success) {
        Logger.log('Logout successful');
      } else {
        Logger.error('Logout failed:', result.message);
      }
    } catch (error) {
      Logger.error('Logout error:', error);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 rounded-lg py-1 pr-2 pl-1 text-left transition-colors hover:bg-ui-hover"
      >
        <Avatar name={name} size="sm" />
        <span className="hidden max-w-44 min-w-0 md:block">
          <span className="block truncate text-sm font-medium text-ui-ink">{name}</span>
          <span className="block truncate text-xs text-ui-ink-3">{role}</span>
        </span>
        <LuChevronDown aria-hidden className={`size-4 text-ui-ink-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute top-full right-0 z-40 mt-2 w-72 animate-enter rounded-xl border border-ui-line bg-ui-card shadow-pop"
        >
          <div className="flex items-center gap-3 border-b border-ui-line-soft px-4 py-4">
            <Avatar name={name} size="md" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ui-ink">{name}</p>
              <p className="truncate text-xs text-ui-ink-2">{role}</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 border-b border-ui-line-soft px-4 py-3">
            <span className="text-sm text-ui-ink-2">Appearance</span>
            <ThemeToggle />
          </div>

          <div className="p-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className="flex h-9 w-full items-center gap-2.5 rounded-lg px-3 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10"
            >
              <LuLogOut aria-hidden className="size-4" />
              Sign out
            </button>
          </div>

          {currentAppVersion && (
            <p className="border-t border-ui-line-soft px-4 py-2 text-[11px] text-ui-ink-3">Version {currentAppVersion}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
